import Brokers from 'bundles/apex/deals/values/brokers';
import ContactRole from 'bundles/apex/deals/values/contact_role';
import DealLink from 'bundles/apex/deals/values/deal_link';
import Incomplete from 'bundles/apex/deals/values/incomplete';
import Link from 'bundles/apex/deals/values/link';
import NextCriticalDate from 'bundles/apex/deals/values/next_critical_date';
import OpenTasks from 'bundles/apex/deals/values/open_tasks';
import UncollectedAmount from 'bundles/apex/deals/values/uncollected_amount';
import StageBadge from 'bundles/apex/deals/stages/badge';

// NOTE: If you need labels, use Apex::Deal.get_stages on the backend as they can be customized
export const stages = {
  pitching: {
    key: 'pitching',
    label: 'Pitching',
  },
  sourcing: {
    key: 'sourcing',
    label: 'Sourcing',
  },
  evaluating: {
    key: 'evaluating',
    label: 'Evaluating',
  },
  transacting: {
    key: 'transacting',
    label: 'Transacting',
  },
  closed: {
    key: 'closed',
    label: 'Closed',
  },
  dead: {
    key: 'dead',
    label: 'Dead',
  },
};

// Configuration for deal values that can be rendered
// Most keys will be attributes that DealSerializer includes, and just provide additional UI-specific config for them
// Others are values with a custom component for rendering that don't necessarily correspond to a single attribute
// Likewise, most keys will be fields, but not all will be (and not all fields will necessarily be here)
// TODO: Seems like Apex::Field could contain more of this data so it is consolidated
// NOTE:
//   Was originally meant to be the definitive source of type info, etc
//   But, is only really used for various tables (including the pipeline)
//   The forms instead did their own thing, and are probably easier to understand
//   TODO: Should the tables work more like the forms?
export const values = {
  address: {},
  address2: {},
  agreementTypeLabel: {},
  brokerGrossCommissionIncome: {
    label: 'Broker Gross',
    currency: true,
    round: true,
  },
  brokers: {
    label: 'Brokers',
    component: Brokers,
  },
  buildingSize: {
    numeric: true,
  },
  buyerTenant: {
    apiAttributes: ['contactRoles'],
    label: 'Buyer / Tenant',
    component: ContactRole,
  },
  capRate: {
    percentage: true,
  },
  closeDate: {},
  closeProbability: {
    percentage: true,
  },
  contractExecutionDate: {
    label: 'Contract / LOI Executed',
  },
  createdAt: {
    label: 'Creation Date',
    date: true,
  },
  deadReason: {},
  dealTypeLabel: {
    label: 'Deal Type',
  },
  displayName: {
    component: DealLink,
    label: 'Deal Title',
  },
  grossCommissionAmount: {
    label: 'Total Gross',
    currency: true,
  },
  grossCommissionIncome: {
    label: 'Brokerage Gross',
    currency: true,
    round: true,
  },
  grossCommissionPercent: {
    label: 'Total Gross %',
    percentage: true,
  },
  id: {
    format: false,
    label: 'Deal ID',
  },
  identifier: {
    component: DealLink,
    label: 'Deal ID',
  },
  incomplete: {
    component: Incomplete,
  },
  leaseExpirationDate: {},
  leaseRate: {
    currency: true,
  },
  leaseStartDate: {},
  leaseTermMonths: {
    label: 'Lease Term (Months)',
    numeric: true,
  },
  leaseTypeLabel: {},
  listingAssociation: {
    label: 'Connected Listing',
    component: Link,
  },
  listingExecutionDate: {
    label: 'Listing / Rep Agreement Executed',
  },
  listingExpirationDate: {
    label: 'Listing / Rep Agreement Expires',
  },
  location: {
    label: 'Location',
  },
  lotSizeAcres: {
    label: 'Lot Size (AC)',
    numeric: true,
  },
  netOperatingIncome: {
    currency: true,
    round: true,
  },
  nextCriticalDate: {
    label: 'Next Critical Date',
    component: NextCriticalDate,
  },
  numberOfUnits: {
    numeric: true,
  },
  occupancyPercent: {
    percentage: true
  },
  openTasks: {
    apiAttributes: ['tasks'],
    component: OpenTasks,
    label: 'Open Tasks',
  },
  outsideCommissionAmount: {
    label: 'Outside Commission',
    currency: true,
  },
  propertyName: {},
  propertySubtypeLabel: {
    label: 'Property Subtype',
  },
  propertyTypeLabel: {
    label: 'Property Type',
  },
  renewalOptionNoticeDate: {},
  requirements: {},
  sellerLandlord: {
    apiAttributes: ['contactRoles'],
    label: 'Seller / Landlord',
    component: ContactRole,
  },
  spaceSizeSf: {
    label: 'Space Size (SF)',
    numeric: true,
  },
  stageLabel: {
    label: 'Deal Stage',
    component: StageBadge,
  },
  transactionValue: {
    label: 'Transaction Value',
    currency: true,
    round: true,
  },
  uncollectedAmount: {
    apiAttributes: ['receivables'],
    label: 'Receivables',
    component: UncollectedAmount,
    currency: true,
  },
  voucherAssociation: {
    label: 'Voucher',
    component: Link,
  },
  weightedBrokerGrossCommissionIncome: {
    label: 'Wtd. Broker Gross',
    currency: true,
    round: true,
  },
  weightedGrossCommissionIncome: {
    label: 'Wtd. Brokerage Gross',
    currency: true,
    round: true,
  },
  yearBuilt: {
    format: false,
  },
  zip: {},
};

for (const key in values) {
  values[key].key = key;
}

// Given fields from the backend, we can further configure values with labels and other information
export function configureValues(fieldsByName) {
  for (const key in values) {
    const value = values[key];

    let field = fieldsByName[key];
    if (!field && /Label$/.test(key)) {
      field = fieldsByName[key.replace(/Label$/, '')];
    }

    if (!value.label) {
      value.label = field?.label;
    }

    if (field?.isDate) {
      value.date = true;
    }
  }

  for (const key in fieldsByName) {
    const field = fieldsByName[key];
    if (field.custom) {
      values[key] = {
        key,
        currency: field.type === 'currency',
        custom: true,
        date: field.type === 'date',
        label: field.label,
        percentage: field.type === 'percentage',
      };
    }
  }
}
